import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import DropdownMenu from './DropdownMenu';
import ConsultationForm from './ConsultationForm';
import logo from '../assets/logo-removebg.png';
import '../styles/Navbar.css';
import '../styles/DropdownMenu.css';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  const navbarRef = useRef(null);
  const lastScrollY = useRef(0);
  const [isVisible, setIsVisible] = useState(true);
  const [showQuoteForm, setShowQuoteForm] = useState(false);

  // Navigation items with dropdowns
  const navItems = [
    { id: 'home', label: 'Home', path: '/' },
    {
      id: 'services',
      label: 'Services',
      path: '/services', // Add direct path to services
      // dropdown: [

      //   { label: 'Modular Interiorsr', path: '/services' },
      //   { label: 'Complete Home Interiors', path: '/services' },
      //   { label: 'Luxury Interiors', path: '/services' },
      //   { label: 'Construction & Renovation', path: '/services' },
      // ],
    },
    {
      id: 'estimator',
      label: 'Estimator',
      path: '/estimator',
      dropdown: [
        { label: 'Full Home Interior', path: '/estimator/full-home' },
        { label: 'Kitchen', path: '/estimator/kitchen' },
        { label: 'Wardrobe', path: '/estimator/wardrobe' },
      ],
    },
    { id: 'reviews', label: 'Reviews', path: '/reviews' },
    { id: 'portfolio', label: 'Portfolio', path: '/portfolio' },
    { id: 'about', label: 'About Us', path: '/about' },
    { id: 'contact', label: 'Contact', path: '/contact' },
    { id: 'hire', label: 'Hire Designer', path: '/hire-designer', className: 'highlight-nav' },
  ];

  // Toggle mobile menu is now defined in the useCallback hook below

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle scroll effect with hide on scroll down
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrolled = currentScrollY > 50;

      // Hide navbar when scrolling down, show when scrolling up
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }

      // Update scrolled state for styling
      setIsScrolled(scrolled);
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setIsMenuOpen(false);
      setActiveDropdown(null);
    }
  }, []);

  // Close all dropdowns when clicking outside
  const handleClickOutside = useCallback((event) => {
    if (navbarRef.current && !navbarRef.current.contains(event.target)) {
      setActiveDropdown(null);
    }
  }, []);

  // Add keyboard and click outside listeners
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleKeyDown, handleClickOutside]);

  // Close mobile menu and reset states on route change
  useEffect(() => {
    const resetNavbar = () => {
      setIsMenuOpen(false);
      setActiveDropdown(null);
      // Smooth scroll to top when navigating
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      // Ensure body scroll is restored on route change
      document.body.style.overflow = '';
    };

    resetNavbar();
  }, [location.pathname]);

  // Toggle mobile menu with focus management
  const toggleMenu = useCallback(() => {
    const newState = !isMenuOpen;
    setIsMenuOpen(newState);

    if (!newState) {
      setActiveDropdown(null);
    }

    // Toggle body scroll immediately (also handled by effect below)
    document.body.style.overflow = newState ? 'hidden' : '';

    // Focus management for accessibility
    if (newState) {
      setTimeout(() => {
        const firstNavItem = document.querySelector('.nav-link, .dropdown-toggle');
        if (firstNavItem) firstNavItem.focus();
      }, 100);
    }
  }, [isMenuOpen]);

  // Keep body overflow in sync with menu state and cleanup on unmount
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Handle dropdown toggle with keyboard support
  const toggleDropdown = useCallback((id, event) => {
    if (event && event.type === 'keydown' && !['Enter', ' '].includes(event.key)) {
      return;
    }

    setActiveDropdown(prevId => prevId === id ? null : id);
  }, []);

  // Handle dropdown hover with keyboard focus
  const handleDropdownHover = useCallback((id) => {
    if (window.innerWidth > 992) { // Desktop only
      setActiveDropdown(id);
    }
  }, []);

  return (
    <nav
      className={`navbar ${isScrolled ? 'scrolled' : ''} ${isVisible ? 'visible' : 'hidden'}`}
      ref={navbarRef}
      aria-label="Main navigation"
    >
      <div className="container">
        {/* Logo */}
        <Link
          to="/"
          className="logo"
          aria-label="Astro Interiors - Go to homepage"
          onClick={() => {
            if (location.pathname === '/') {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
        >
          <img
            src={logo}
            alt=""
            className="logo-img"
            aria-hidden="true"
          />
          {/* <span className="sr-only">Astro Interiors - Vastu Perfect Homes</span> */}
        </Link>

        {/* Desktop Navigation */}
        <div className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
          {navItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path) && item.path !== '/';
            return item.dropdown ? (
              <div
                key={item.id}
                className="dropdown-wrapper"
                onMouseEnter={() => handleDropdownHover(item.id)}
                onMouseLeave={() => window.innerWidth > 992 && setActiveDropdown(null)}
              >
                <DropdownMenu
                  title={
                    item.path ? (
                      <Link
                        to={item.path}
                        className={`dropdown-toggle ${isActive ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsMenuOpen(false);
                        }}
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <span className="dropdown-toggle">
                        {item.label}
                      </span>
                    )
                  }
                  items={item.dropdown}
                  isOpen={activeDropdown === item.id}
                  onClick={(e) => toggleDropdown(item.id, e)}
                  onMouseEnter={() => handleDropdownHover(item.id)}
                  onMouseLeave={() => window.innerWidth > 992 && setActiveDropdown(null)}
                />
              </div>
            ) : (
              <Link
                key={item.id}
                to={item.path}
                className={`nav-link ${location.pathname === item.path ? 'active' : ''} ${item.className || ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </Link>
            )
          })}

          <button
            className="btn btn-primary quote-btn"
            onClick={(e) => {
              e.preventDefault();
              setShowQuoteForm(true);
              setIsMenuOpen(false);
            }}
            aria-label="Get Free Quote"
          >
            Get Free Quote
            <span className="btn-arrow" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>

          <ConsultationForm
            isOpen={showQuoteForm}
            onClose={() => setShowQuoteForm(false)}
            type="quote"
          />
        </div>

        {/* Mobile Menu Button */}
        <button
          className={`hamburger ${isMenuOpen ? 'active' : ''}`}
          onClick={toggleMenu}

          aria-expanded={isMenuOpen}
          aria-controls="main-navigation"
        >
          <span className="hamburger-box">
            <span className="hamburger-inner"></span>
          </span>
          {/* <span className="sr-only">{isMenuOpen ? 'Close menu' : ''}</span> */}
        </button>
      </div>
    </nav>
  );
};

export default Navbar;
